
import { useState, useEffect, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import { toast } from "@/components/ui/sonner";
import { calculateDecisionPoints, scheduleDecision, clearDecisionTimers } from './simulation/decisionScheduler';
import { DecisionTimerPoints } from './simulation/types';

export const useDecisionEvents = (
  yearDuration: number, 
  isSimulating: boolean,
  decisionsThrottled: boolean,
  setDecisionsThrottled: React.Dispatch<React.SetStateAction<boolean>>,
  decisionTimerFired: Record<string, boolean>,
  setDecisionTimerFired: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
) => {
  const { 
    selectedRole,
    careerLevel, 
    yearsPassed,
    currentChallenge,
    currentSpecialEvent,
    skills,
    setCurrentChallenge
  } = useGame();

  const [lastDecisionTime, setLastDecisionTime] = useState<number | null>(null);
  const decisionTimersRef = useRef<NodeJS.Timeout[]>([]);
  
  // Add recent challenges tracking to prevent repetition
  const [recentChallenges, setRecentChallenges] = useState<string[]>([]);
  
  // CRITICAL FIX: Log when simulation starts/stops to debug timing issues
  useEffect(() => {
    console.log('Simulation state changed:', { isSimulating, yearsPassed });
  }, [isSimulating, yearsPassed]);

  // Reset the decision timers fired state when starting a new simulation
  useEffect(() => {
    if (isSimulating) {
      // CRITICAL FIX: Clear the decision timer state when starting a new simulation year
      console.log('Resetting decision timer state for new simulation');
      setDecisionTimerFired({});
    }
  }, [isSimulating, yearsPassed, setDecisionTimerFired]);

  // Reset decisions throttling after a period of time
  useEffect(() => {
    if (decisionsThrottled) {
      // Add a maximum throttle time to prevent getting permanently stuck
      const timer = setTimeout(() => {
        setDecisionsThrottled(false);
        console.log("Decision throttle timeout reset");
      }, 10000); // Max 10 seconds throttle to prevent getting stuck
      
      return () => clearTimeout(timer);
    }
  }, [decisionsThrottled, setDecisionsThrottled]);

  // Reset decisions throttling when a challenge is completed
  useEffect(() => {
    if (!currentChallenge && decisionsThrottled) {
      // Add a delay before allowing new decisions after completing one
      const timer = setTimeout(() => {
        setDecisionsThrottled(false);
        console.log("Decision throttle reset after challenge completion");
      }, 3000); // 3 second cooldown after completing a decision
      
      return () => clearTimeout(timer);
    }
  }, [currentChallenge, decisionsThrottled, setDecisionsThrottled]);

  // Clean up all timers when component unmounts or simulation stops
  useEffect(() => {
    return () => {
      // Clear decision timers
      clearDecisionTimers(decisionTimersRef.current);
    };
  }, []);

  // Cleanup timers when simulation stops
  useEffect(() => {
    if (!isSimulating) {
      // Clear decision timers when simulation stops
      clearDecisionTimers(decisionTimersRef.current);
      decisionTimersRef.current = [];
    }
  }, [isSimulating]);

  // Function to schedule decision events at specific times
  const scheduleDecisionEvents = () => {
    // Clear any existing decision timers
    clearDecisionTimers(decisionTimersRef.current);
    decisionTimersRef.current = [];
    
    // Schedule decisions at calculated time points
    const decisionPoints = calculateDecisionPoints(yearDuration);
    console.log('Calculated decision points:', decisionPoints, 'for year duration:', yearDuration);
    
    // CRITICAL FIX: Create a unique key for this specific year's simulation
    const yearSimulationKey = `year_${yearsPassed}_simulation_${Date.now()}`;
    console.log('Creating new simulation with key:', yearSimulationKey);
    
    decisionPoints.forEach((timePoint, index) => {
      // CRITICAL FIX: Create more meaningful timer IDs for debugging
      const timerLabel = `Decision_Timer_Year${yearsPassed}_Point${index}_Time${timePoint}`;
      
      console.log(`Scheduling decision at ${timePoint}s for ${timerLabel}`);
      
      const timerId = setTimeout(() => {
        console.log(`Timer fired for ${timerLabel}`);
        
        if (isSimulating) {
          scheduleDecision(
            timePoint, 
            index, 
            yearsPassed, 
            currentChallenge, 
            currentSpecialEvent, 
            decisionsThrottled, 
            decisionTimerFired,
            recentChallenges,
            selectedRole,
            careerLevel,
            skills,
            setCurrentChallenge,
            setDecisionTimerFired,
            setRecentChallenges
          );
        } else {
          console.log(`${timerLabel} - Not simulating anymore`);
        }
      }, timePoint * 1000); // Convert to milliseconds
      
      decisionTimersRef.current.push(timerId);
    });
  };

  return {
    decisionTimerFired,
    setDecisionTimerFired,
    lastDecisionTime,
    setLastDecisionTime,
    decisionTimersRef,
    scheduleDecisionEvents,
    recentChallenges,
    setRecentChallenges
  };
};
